import { DownOutlined, MenuFoldOutlined, MenuOutlined, MenuUnfoldOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Button, Dropdown } from "../ui";
import { NavigationDrawer } from "../components/DrawerSurfaces";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { appShellMetrics } from "./spacing";
import { AppShellCredits } from "./AppShellCredits";
import { AppShellUserMenu, type AppShellUserMenuProps } from "./AppShellUserMenu";
import { AppShellNavigationFooter, type AppShellNavigationFooterProps } from "./AppShellNavigationFooter";
import { AppShellContextControls } from "./AppShellContextControls";
import { AppShellContextSelector } from "./AppShellContextSelector";
import type { AppShellContextConfiguration } from "./appShellContext";
import { AppShellSetupHelp } from "./AppShellSetupHelp";
import { AsyncActionButton } from "../components/AsyncActionButton";
import { AppShellPinnedPages, type AppShellPinnedPagesProps } from "./AppShellPinnedPages";
import { AppShellPinButton, type AppShellPinButtonProps } from "./AppShellPinButton";
import { QeddyLauncher, type QeddyLauncherProps } from "../help/QeddyLauncher";
import { GlobalCriticalIssuesBanner, type GlobalCriticalIssuesBannerProps } from "../feedback/GlobalCriticalIssuesBanner";
import "./app-shell.css";
import "./app-frame.css";
import { AppShellDesktopWindowMenu, AppShellDesktopCaptionButtons, type AppShellDesktopConfiguration } from "./AppShellDesktop";
import { AppShellDesktopActions } from "./AppShellDesktopActions";
import { accordionOpenKeys, renderAppShellMenuItems, type AppShellMenuItem, type AppShellNavigation } from "./appShellMenu";
import { AppShellIcon } from "./AppShellIcon";
import { AttentionIndicator, type AttentionTone } from "../ui/AttentionIndicator";
import { AppShellOverlayHostContext } from "./appShellOverlayHost";
import type { AppShellIconKey } from "./appShellIcons";
import { useQeddyProvider } from "../help/QeddyProvider";
import { useAppShellScrollRestoration, type AppShellContentLocation } from "./useAppShellScrollRestoration";
import { ThemedModal } from "../components/ThemedModal";
import { PageHeaderNavigationProvider } from "./PageHeaderNavigationProvider";
import { AppShellWorkspaceContext, type AppShellWorkspaceRequest } from "./appShellWorkspace";

const SEARCH_SHORTCUT_KEY = "k";

export type AppShellAction = { label: string; onClick: () => void; disabled?: boolean };
export type AppShellMobileTab = { key: string; label: string; icon: AppShellIconKey; attention?: { label: string; count: number; tone?: AttentionTone } };
export type AppShellMobileTabs = { items: AppShellMobileTab[]; selectedKey?: string; onSelect: (key: string) => void; menuLabel: string; menuAttention?: { label: string; count: number; tone?: AttentionTone } };
/**
 * Einstieg in eine produktweite Suche. Der Rahmen zeigt nur den Einstieg (Eintrag im Fußbereich der Navigation
 * über der Tarifkarte, eingeklappt als Symbol, mobil als Lupe in der oberen Leiste) und das Tastenkürzel
 * Strg+K / ⌘K; den Suchdialog selbst und was er findet, liefert das Produkt.
 */
export type AppShellSearch = { label: string; shortcutHint?: string; onOpen: () => void };
export type AppShellBanner = { type: "info" | "success" | "warning" | "error"; message: string; description?: string; action?: AppShellAction };

export type AppShellProps = {
  /**
   * Marke: Bildmarke (`logo`), daneben Wortmarke (Bild) oder Name, optional Unterzeile. `markTile` ist Produktstandard:
   * Bildmarke auf heller Kachel, die mit dem Logo mitwächst (für Logos, die auf der Leiste nicht tragen); ohne Angabe
   * steht das Logo frei auf der Leiste. `home` macht den ganzen Markenbereich zum Weg zur Startseite des Produkts
   * (Dashboard); `label` ist der zugängliche Name.
   * `compactLogo` ersetzt mobil unter 380 Pixeln Bildmarke und Schriftzug durch die schmale Produktmarke.
   */
  /** `fullLogo`: Produktstandard für Marken mit Volllogo – ausgeklappt nur dieses Bild (ohne Bildmarke, Name, Untertitel), eingeklappt die Bildmarke `logo`. */
  brand: { name: string; logo: string; compactLogo?: string; wordmark?: string; fullLogo?: string; subtitle?: string; markTile?: boolean; home?: AppShellAction };
  navigation: AppShellNavigation;
  mobileNavigationControlled?: boolean;
  /** Mobil: Hauptziele als Leiste am unteren Rand (DNA D24 „Daumenzone“); der letzte Eintrag öffnet die Navigation. */
  mobileTabs?: AppShellMobileTabs;
  navigationWidth: number;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  mobile: boolean;
  mobileNavigationOpen: boolean;
  onMobileNavigationOpenChange: (open: boolean) => void;
  labels: { expandNavigation: string; collapseNavigation: string };
  /** Fußbereich der Navigation, in allen Apps gleich: Tarifkarte und Rechtstexte (Daten und Texte vom Produkt). */
  navigationFooter?: AppShellNavigationFooterProps;
  /** Optionale Elemente; fehlende reservieren keinen Platz. Nur Pinleiste oder Kontextauswahl (bzw. Desktop-Hülle) erzeugen die durchgehende obere Zeile; Hilfe und Einrichtungshilfe stehen sonst neben dem Benutzermenü. */
  pinnedPages?: Omit<AppShellPinnedPagesProps, "compact">;
  pinAction?: AppShellPinButtonProps;
  context?: AppShellContextConfiguration;
  search?: AppShellSearch;
  userMenu: AppShellUserMenuProps;
  credits?: AppShellAction & { balance: string; hint?: string };
  help?: QeddyLauncherProps | true;
  setupHelp?: AppShellAction;
  criticalBanner?: GlobalCriticalIssuesBannerProps;
  banners?: AppShellBanner[];
  children: ReactNode;
  /** Native host provides bridge callbacks; Shared owns the window controls and drag regions. */
  desktop?: AppShellDesktopConfiguration;
  contentStyle?: CSSProperties;
  contentLocation?: AppShellContentLocation;
  fillViewport?: boolean;
  /**
   * Alle Seiten nutzen die verfügbare Inhaltsbreite mit Seitenrand.
   * Beide Werte bleiben für bestehende Produktanbindungen kompatibel; `full` ist nicht mehr erforderlich.
   */
  contentWidth?: "default" | "full";
};

function AttentionMark({ attention, dot = false }: { attention: AppShellMenuItem["attention"]; dot?: boolean }) {
  if (!attention) return null;
  return <AttentionIndicator {...attention} dot={dot} className={`mq-frame-count${dot ? " mq-frame-count--dot" : ""}`} />;
}

function sumAttention(items: AppShellMenuItem[]): number {
  return items.reduce((sum, item) => sum + (item.attention?.count ?? 0) + (item.children ? sumAttention(item.children) : 0), 0);
}

/** Eine Gruppe zeigt die Fehlerfarbe, sobald ein gezählter Eintrag darin ein Fehler ist. */
function hasErrorAttention(items: AppShellMenuItem[]): boolean {
  return items.some((item) => (item.attention?.tone === "error" && (item.attention.count ?? 0) > 0) || (item.children ? hasErrorAttention(item.children) : false));
}

type FrameNavProps = {
  navigation: AppShellNavigation;
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  compact: boolean;
  onExpand: () => void;
  label: string;
};

/**
 * Navigationsbaum aus den serialisierbaren Navigationsdaten: Abschnitte (`type: "group"`) mit Überschrift,
 * aufklappbare Gruppen (immer nur eine offen), Zähler. Eingeklappt nur Symbole; eine Gruppe klappt dann die
 * Navigation auf.
 */
function FrameNav({ navigation, openKeys, onOpenChange, compact, onExpand, label }: FrameNavProps) {
  const selected = new Set(navigation.selectedKeys ?? []);
  const click = (item: AppShellMenuItem, parents: string[], event: MouseEvent) => {
    // Gleiche Form wie das Klickereignis des bisherigen Antd-Menüs; Aufrufer lesen `key` und `keyPath`.
    navigation.onClick?.({ key: item.key, keyPath: [item.key, ...parents], domEvent: event });
  };
  const toggle = (item: AppShellMenuItem) => {
    item.onTitleClick?.();
    if (compact) { onExpand(); onOpenChange([...openKeys.filter((key) => key !== item.key), item.key]); return; }
    onOpenChange(openKeys.includes(item.key) ? openKeys.filter((key) => key !== item.key) : [...openKeys, item.key]);
  };
  const entries = (items: AppShellMenuItem[], parents: string[]): ReactNode => items.map((item) => {
    if (item.type === "divider") return <li key={item.key} className="mq-frame-nav-divider" role="separator" />;
    if (item.type === "group") {
      return (
        <li key={item.key} className="mq-frame-nav-section">
          {item.label ? <div className="mq-frame-nav-heading">{item.label}</div> : null}
          <ul className="mq-frame-nav-list">{entries(item.children ?? [], parents)}</ul>
        </li>
      );
    }
    const icon = item.icon ? <span className="mq-frame-nav-icon" aria-hidden><AppShellIcon name={item.icon} /></span> : <span className="mq-frame-nav-icon" aria-hidden />;
    if (item.children) {
      const open = openKeys.includes(item.key) && !compact;
      const bodyId = `mq-frame-nav-${item.key}`;
      const count = sumAttention(item.children);
      return (
        <li key={item.key} className="mq-frame-nav-group" data-open={open}>
          <button type="button" className="mq-frame-nav-item" aria-expanded={open} aria-controls={bodyId} disabled={item.disabled} title={compact ? item.label : undefined} onClick={() => toggle(item)}>
            {icon}
            <span className="mq-frame-nav-label">{item.label}</span>
            {count > 0 ? <AttentionMark attention={{ tone: hasErrorAttention(item.children) ? "error" : "attention", label: String(count), count }} dot={compact} /> : null}
            <DownOutlined className="mq-frame-nav-chevron" aria-hidden />
          </button>
          <div className="mq-frame-nav-group-body" id={bodyId} inert={!open}>
            <ul className="mq-frame-nav-list">{entries(item.children, [item.key, ...parents])}</ul>
          </div>
        </li>
      );
    }
    return (
      <li key={item.key}>
        <button type="button" className={`mq-frame-nav-item${item.danger ? " mq-frame-nav-item--danger" : ""}`} aria-current={selected.has(item.key) ? "page" : undefined} disabled={item.disabled} title={compact ? item.label : undefined} onClick={(event) => click(item, parents, event)}>
          {icon}
          <span className="mq-frame-nav-label">{item.label}</span>
          <AttentionMark attention={item.attention} dot={compact} />
        </button>
      </li>
    );
  });
  return (
    <nav className="mq-frame-nav" aria-label={label}>
      <ul className="mq-frame-nav-list">{entries(navigation.items, [])}</ul>
    </nav>
  );
}

function Brand({ brand, showName = true, onNavigate }: { brand: AppShellProps["brand"]; showName?: boolean; onNavigate?: () => void }) {
  const home = brand.home;
  const content = brand.fullLogo && showName ? <img className="mq-frame-brand-full-logo" src={brand.fullLogo} alt={brand.name} /> : (
    <>
      <span className={brand.markTile ? "mq-frame-brand-mark mq-frame-brand-mark--tile" : "mq-frame-brand-mark"}><img src={brand.logo} alt="" /></span>
      {brand.compactLogo ? <img className="mq-frame-brand-compact-logo" src={brand.compactLogo} alt="" /> : null}
      {showName ? (
        <span className="mq-frame-brand-text">
          {brand.wordmark ? <img className="mq-frame-brand-wordmark" src={brand.wordmark} alt={brand.name} /> : <span className="mq-frame-brand-name">{brand.name}</span>}
          {brand.subtitle ? <span className="mq-frame-brand-sub">{brand.subtitle}</span> : null}
        </span>
      ) : null}
    </>
  );
  const brandClass = `mq-frame-brand${brand.compactLogo ? " mq-frame-brand--responsive" : ""}`;
  if (!home) return <div className={brandClass}>{content}</div>;
  return (
    <button type="button" className={`${brandClass} mq-frame-brand--home`} aria-label={home.label} title={home.label} disabled={home.disabled}
      onClick={() => { home.onClick(); return onNavigate?.(); }}>
      {content}
    </button>
  );
}

/**
 * Ein App-Rahmen für alle Produkte (Plan plans/2026-10-13-applayout-gemeinsam.md): Aufbau und Maße nach dem
 * my-vitalteam-Rahmen — Navigation links mit Markenkachel, Gruppen und Zählern, Fußbereich; Benutzermenü (nur Symbol)
 * oben rechts; mobil Kopfzeile, Leiste unten und Menü-Blatt. Nur mit Pinleiste, Kontextauswahl oder Desktop-Hülle
 * entsteht eine durchgehende obere Zeile über Navigation und Inhalt. Farben und Schriften aus
 * dem Theme.
 */
export function AppShell({
  brand, navigation, navigationWidth, collapsed, onCollapsedChange, mobile,
  mobileNavigationOpen, onMobileNavigationOpenChange, labels, mobileNavigationControlled = false,
  pinnedPages, pinAction, context, userMenu, credits, help: helpConfiguration, setupHelp,
  criticalBanner, banners, children, desktop, contentStyle, fillViewport = false, contentWidth = "default",
  navigationFooter, mobileTabs, search, contentLocation,
}: AppShellProps) {
  const { mainRef, contentRef, onScroll } = useAppShellScrollRestoration(contentLocation);
  const [contextPickerOpen, setContextPickerOpen] = useState(false);
  const [localOpenKeys, setLocalOpenKeys] = useState<string[]>([]);
  const controlledNavigation = navigation.openKeys !== undefined && (!mobile || mobileNavigationControlled);
  const openKeys = accordionOpenKeys(navigation.items, controlledNavigation ? navigation.openKeys ?? [] : localOpenKeys);
  const onOpenChange = (keys: string[]) => {
    const next = accordionOpenKeys(navigation.items, keys, openKeys);
    if (controlledNavigation) navigation.onOpenChange?.(next);
    else setLocalOpenKeys(next);
  };
  const qeddy = useQeddyProvider();
  const help = helpConfiguration === true ? qeddy?.launcher : helpConfiguration;
  const criticalBannerRef = useRef<HTMLDivElement>(null);
  const [overlayHost, setOverlayHost] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const banner = criticalBannerRef.current;
    if (!banner) return;
    let frame: number | null = null;
    const updateHeight = () => {
      frame = null;
      const height = `${Math.ceil(banner.getBoundingClientRect().height)}px`;
      if (document.documentElement.style.getPropertyValue("--critical-issue-banner-height") !== height) {
        document.documentElement.style.setProperty("--critical-issue-banner-height", height);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(() => {
      if (frame === null) frame = window.requestAnimationFrame(updateHeight);
    });
    observer.observe(banner);
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.documentElement.style.removeProperty("--critical-issue-banner-height");
    };
  }, []);

  const onOpenSearch = search?.onOpen;
  useEffect(() => {
    if (!onOpenSearch) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (typeof event.key !== "string" || event.key.toLowerCase() !== SEARCH_SHORTCUT_KEY || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      // Über einem offenen Dialog bleibt das Kürzel beim Dialog; die Suche öffnet sich nicht darüber.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      event.preventDefault();
      onOpenSearch();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onOpenSearch]);

  // Angeforderte Arbeitsfläche (useAppShellWorkspace) klappt die Navigation ein, ohne den Produktzustand zu ändern.
  const [workspace, setWorkspace] = useState({ requests: 0, focus: 0, expanded: false });
  const requestWorkspace = useCallback(({ focus }: AppShellWorkspaceRequest) => {
    setWorkspace((current) => ({ ...current, requests: current.requests + 1, focus: current.focus + (focus ? 1 : 0) }));
    return () => setWorkspace((current) => {
      const requests = current.requests - 1;
      return { requests, focus: current.focus - (focus ? 1 : 0), expanded: requests > 0 && current.expanded };
    });
  }, []);
  const workspaceActive = workspace.requests > 0;
  const navigationCollapsed = workspaceActive ? !workspace.expanded : collapsed;
  const changeCollapsed = (next: boolean) => {
    if (workspaceActive) setWorkspace((current) => ({ ...current, expanded: !next }));
    else onCollapsedChange(next);
  };

  const compact = navigationCollapsed && !mobile;
  const hasTopRow = !mobile && Boolean(desktop || pinnedPages || pinAction || context);
  const navLabel = labels.expandNavigation;

  const contextActions = context?.actions ? (
      <Dropdown trigger={["click"]} menu={{ items: renderAppShellMenuItems(context.actions.items), onClick: ({ key }) => context.actions?.onAction(String(key)) }}>
        <AsyncActionButton icon={<MoreOutlined />} loading={context.actions.loading} aria-label={context.actions.label} />
      </Dropdown>
    ) : undefined;
  const contextControls = context ? (
    <AppShellContextControls actions={contextActions}>
      {context.selections.map((selection, index) => <AppShellContextSelector key={index} {...selection} />)}
    </AppShellContextControls>
  ) : null;

  // Fester Platz unten statt im Menü: Die Suche bleibt bei jeder Menülänge ohne Scrollen an derselben Stelle.
  const searchEntry = search && !mobile ? (
    <button type="button" className="mq-frame-nav-item mq-frame-search-entry" onClick={search.onOpen} aria-label={search.label}
      title={search.shortcutHint ? `${search.label} (${search.shortcutHint})` : search.label}>
      <span className="mq-frame-nav-icon" aria-hidden><SearchOutlined /></span>
      <span className="mq-frame-nav-label">{search.label}</span>
      {search.shortcutHint && !compact ? <kbd className="mq-frame-search-kbd">{search.shortcutHint}</kbd> : null}
    </button>
  ) : null;
  const footerContent = searchEntry || navigationFooter || credits ? (
    <div className="mq-frame-sidebar-footer">
      {searchEntry}
      {navigationFooter ? <AppShellNavigationFooter {...navigationFooter} compact={compact} /> : null}
      {credits ? <AppShellCredits {...credits} compact={compact} /> : null}
    </div>
  ) : null;

  const nav = <FrameNav navigation={navigation} openKeys={openKeys} onOpenChange={onOpenChange} compact={compact} onExpand={() => changeCollapsed(false)} label={navLabel} />;
  const toggle = (
    <button type="button" className="mq-frame-icon-btn mq-frame-collapse" onClick={() => changeCollapsed(!navigationCollapsed)} aria-label={navigationCollapsed ? labels.expandNavigation : labels.collapseNavigation} title={navigationCollapsed ? labels.expandNavigation : labels.collapseNavigation}>
      {navigationCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
    </button>
  );

  const searchIcon = search ? (
    <button type="button" className="mq-frame-icon-btn mq-frame-search-icon" onClick={search.onOpen} aria-label={search.label} title={search.shortcutHint ? `${search.label} (${search.shortcutHint})` : search.label}>
      <SearchOutlined />
    </button>
  ) : null;

  const bannerList = banners?.map((banner, index) => <Alert key={index} banner showIcon type={banner.type} message={banner.message} description={banner.description || banner.action ? <>{banner.description}{banner.action ? <> <Button type="link" onClick={banner.action.onClick} disabled={banner.action.disabled}>{banner.action.label}</Button></> : null}</> : undefined} />);
  const content = (
    <div ref={contentRef} onScroll={onScroll} className={`mq-frame-content app-content${fillViewport ? " mq-frame-content--fill" : ""}${contentWidth === "full" ? " mq-frame-content--full" : ""}${hasTopRow ? " mq-frame-content--below-top" : ""}`} style={mobile && mobileTabs ? { ...contentStyle, paddingBottom: "var(--mq-frame-tabbar-clearance)" } : contentStyle}>
      <div className="mq-app-page"><PageHeaderNavigationProvider><AppShellOverlayHostContext.Provider value={mobile ? null : overlayHost}><AppShellWorkspaceContext.Provider value={requestWorkspace}>{children}</AppShellWorkspaceContext.Provider></AppShellOverlayHostContext.Provider></PageHeaderNavigationProvider></div>
    </div>
  );

  const rootClass = ["mq-app-shell", "mq-frame", mobile ? "mq-frame--mobile" : "", compact ? "mq-frame--collapsed" : "", hasTopRow ? "mq-frame--with-top" : "", workspace.focus > 0 ? "mq-frame--focus" : "", mobile && mobileTabs ? "mq-app-shell--with-tabbar" : ""].filter(Boolean).join(" ");
  const rootStyle = { "--mq-frame-nav-width": `${navigationWidth}px`, "--mq-frame-nav-collapsed": `${appShellMetrics.collapsedWidth}px` } as CSSProperties;

  return (
    <>
      <div ref={criticalBannerRef} style={{ position: "relative", zIndex: appShellMetrics.criticalBannerZIndex }}>{criticalBanner ? <GlobalCriticalIssuesBanner {...criticalBanner} /> : null}</div>
      {mobile ? (
        <div className={rootClass} style={rootStyle}>
          <header className="mq-frame-topbar">
            {/* Mit Kontextauswahl bleibt oben Platz für die Aktionen; die Auswahl steht in einer eigenen Zeile. */}
            <Brand brand={brand} showName={!contextControls || Boolean(context?.mobilePicker)} />
            <span className="mq-frame-spacer" />
            {context?.mobilePicker ? <button type="button" className="mq-frame-icon-btn" aria-label={context.mobilePicker.label}
              title={context.selections.map(selection => selection.label).join(" · ")} aria-haspopup="dialog" onClick={() => setContextPickerOpen(true)}><AppShellIcon name={context.mobilePicker.icon} /></button> : null}
            {searchIcon}
            {help ? <QeddyLauncher {...help} /> : null}
            <AppShellUserMenu {...userMenu} legal={userMenu.legal ?? navigationFooter?.legal} />
            {/* Mit unterer Leiste öffnet deren „Menü“ die Navigation; ein zweites Menüsymbol oben entfällt. */}
            {mobileTabs ? null : <button type="button" className="mq-frame-icon-btn" onClick={() => onMobileNavigationOpenChange(true)} aria-label={labels.expandNavigation}><MenuOutlined /></button>}
          </header>
          {contextControls && !context?.mobilePicker ? <div className="mq-frame-mobile-context">{contextControls}</div> : null}
          {context?.mobilePicker ? <ThemedModal open={contextPickerOpen} title={context.mobilePicker.label} headerExtra={contextActions} onCancel={() => setContextPickerOpen(false)} footer={null} autoFocusFirstField={false} submitOnEnter={false}>
            {context.selections.map((selection, index) => <AppShellContextSelector key={index} {...selection} presentation="list" onChange={value => {
              selection.onChange(value);
              if (context.selections.length === 1) setContextPickerOpen(false);
            }} />)}
          </ThemedModal> : null}
          {pinnedPages ? <div className="mq-frame-mobile-pins"><AppShellPinnedPages {...pinnedPages} compact /></div> : null}
          <main ref={mainRef} onScroll={onScroll} className="mq-frame-main">{bannerList}{content}</main>
          {mobileTabs ? (
            <nav className="mq-frame-tabbar" aria-label={mobileTabs.menuLabel}>
              {mobileTabs.items.map((tab) => (
                <button key={tab.key} type="button" className="mq-frame-tab" aria-current={mobileTabs.selectedKey === tab.key ? "page" : undefined} onClick={() => mobileTabs.onSelect(tab.key)}>
                  <span className="mq-frame-tab-icon"><AppShellIcon name={tab.icon} />{tab.attention ? <AttentionIndicator {...tab.attention} tone={tab.attention.tone ?? "attention"} dot className="mq-frame-count mq-frame-count--dot" /> : null}</span>
                  <span className="mq-frame-tab-label">{tab.label}</span>
                </button>
              ))}
              <button type="button" className="mq-frame-tab" aria-current={mobileTabs.items.some((tab) => tab.key === mobileTabs.selectedKey) ? undefined : "page"} aria-haspopup="dialog" onClick={() => onMobileNavigationOpenChange(true)}>
                <span className="mq-frame-tab-icon"><MenuOutlined />{mobileTabs.menuAttention ? <AttentionIndicator {...mobileTabs.menuAttention} tone={mobileTabs.menuAttention.tone ?? "attention"} dot className="mq-frame-count mq-frame-count--dot" /> : null}</span>
                <span className="mq-frame-tab-label">{mobileTabs.menuLabel}</span>
              </button>
            </nav>
          ) : null}
          <NavigationDrawer open={mobileNavigationOpen} onClose={() => onMobileNavigationOpenChange(false)} className="mq-frame-sheet" styles={{ body: { padding: 0 } }} aria-label={navLabel}>
            <div className="mq-frame-sheet-inner mq-app-shell-mobile-menu">
              <div className="mq-frame-sheet-grip" aria-hidden />
              <Brand brand={brand} onNavigate={() => onMobileNavigationOpenChange(false)} />
              {nav}
              {footerContent}
            </div>
          </NavigationDrawer>
        </div>
      ) : (
        <div className={rootClass} style={rootStyle}>
          {hasTopRow ? (
            <header className={`mq-frame-top${desktop ? " mu-app-shell-header--desktop" : ""}`}>
              <AppShellDesktopActions enabled={Boolean(desktop)} notifyBlocked={desktop?.notifyInteractionBlocked}>
                {desktop ? <AppShellDesktopWindowMenu menu={desktop.menu} /> : <Brand brand={brand} />}
                {pinnedPages ? <AppShellPinnedPages {...pinnedPages} compact={false} /> : null}
                <div className={`mq-frame-top-actions${desktop ? " mu-desktop-no-drag" : ""}`}>
                  {pinAction ? <AppShellPinButton {...pinAction} /> : null}
                  {contextControls}
                  {help ? <QeddyLauncher {...help} /> : null}
                  {setupHelp ? <AppShellSetupHelp {...setupHelp} /> : null}
                  <AppShellUserMenu {...userMenu} legal={userMenu.legal ?? navigationFooter?.legal} />
                </div>
              </AppShellDesktopActions>
              {desktop?.caption ? <AppShellDesktopCaptionButtons caption={desktop.caption} /> : null}
            </header>
          ) : null}
          <aside className="mq-frame-sidebar mq-app-shell-navigation">
            {/* Ohne durchgehende obere Zeile steht die Marke in einem Kopf mit deren Höhe; das Einklapp-Symbol
                liegt in beiden Fällen in eigener Zeile darunter an derselben Stelle. */}
            {hasTopRow ? null : <div className="mq-frame-sidebar-head"><Brand brand={brand} showName={!compact} /></div>}
            <div className="mq-frame-sidebar-toggle">{toggle}</div>
            {nav}
            {footerContent}
          </aside>
          <main ref={mainRef} onScroll={onScroll} className="mq-frame-main">
            {hasTopRow ? null : (
              <div className="mq-frame-main-header">
                {help ? <QeddyLauncher {...help} /> : null}
                {setupHelp ? <AppShellSetupHelp {...setupHelp} /> : null}
                <AppShellUserMenu {...userMenu} legal={userMenu.legal ?? navigationFooter?.legal} />
              </div>
            )}
            {bannerList}
            {content}
          </main>
          {/* Vollflächen (WizardOverlay) liegen über dem Inhaltsbereich, nicht über Navigation und oberer Zeile. */}
          <div ref={setOverlayHost} className="mq-frame-overlay-host" />
        </div>
      )}
    </>
  );
}
